import React from 'react';
import ReactDOM from 'react-dom/client';
import { XSButton, XSButtonInput } from '../button/XSButton';
import './XSDialog.css';
import { XSSection, XSSectionInput } from '../section/XSSection';
import { XSBox, XSBoxInput } from '../box/XSBox';
import { XSText } from '../text/XSText';
import clsx, { ClassValue } from 'clsx';
import { XSIcon } from '../icon/XSIcon';
import { XSMask } from '../mask/XSMask';
import { XSDisableScroll } from '../disable-scroll/XSDisableScroll';
import { RecoilRoot } from 'recoil';
import { XSCard } from '../card/XSCard';
import { BrowserRouter, HashRouter } from 'react-router-dom';

const componentRootClassName = '.XSDialog';
const Router: any = document.documentElement.classList.contains('pos') ? HashRouter : BrowserRouter;

export namespace XSDialog {
    type ShowOptions = {
        className?: ClassValue;
        size?: 'fullScreen' | 'fitContent' | 'default';
        background?: 'card' | 'transparent';
        onClose?(): void;
        maxWidth?: number;
        closeOnBackdropClick?: boolean;
    };

    export function show(element: JSX.Element, options?: ShowOptions) {
        const elementToAppend = document.createElement('div');
        const isDesktop = window.matchMedia('(hover:hover)').matches;
        elementToAppend.classList.add('XSDialog-root');

        Object.assign(elementToAppend, {
            _close() {
                Object.assign(elementToAppend, { _close: undefined });
                close();
            },
        });

        const rootElement = document.querySelector('#XSDialog-container') || document.querySelector('body')?.parentNode;

        rootElement?.appendChild(elementToAppend);
        const root = ReactDOM.createRoot(elementToAppend);
        const classes: string[] = ['XSDialog'];
        const style: React.CSSProperties = {};

        if (options?.maxWidth) {
            style['--max-width'] = `${options.maxWidth}px`;
        }

        let size = options?.size;

        if (!size && isDesktop) {
            size = 'fitContent';
        } else if (!size) {
            size = 'default';
        }

        classes.push(`size-${size}`);

        root.render(
            <RecoilRoot>
                <Router>
                    <XSMask className={'XSDialog-mask'} />

                    <div
                        onClick={() => {
                            if (options?.closeOnBackdropClick === false) {
                                return;
                            }

                            close();
                        }}
                        className={clsx(options?.className, classes)}
                        style={style}>
                        <div
                            onClick={(event) => event.stopPropagation()}
                            className={clsx('XSDialog-container', {
                                transparent: options?.background === 'transparent',
                            })}>
                            <ErrorBoundary
                                onError={() => {
                                    const ref = elementToAppend.querySelector('.XSDialog') as any;

                                    if (ref) {
                                        ref.style.setProperty('--max-width', '640px');
                                    }
                                }}>
                                {element}
                            </ErrorBoundary>

                            <XSDisableScroll />
                        </div>
                    </div>
                </Router>
            </RecoilRoot>,
        );

        let unmounted = false;

        function close() {
            const unmount = () => {
                if (unmounted) {
                    return;
                }

                unmounted = true;

                root.unmount();

                try {
                    elementToAppend.remove();
                } catch (error) {
                    console.error(error);
                }

                options?.onClose?.();
            };

            elementToAppend.querySelector(componentRootClassName)?.classList.add('unmounting');
            elementToAppend.querySelector('.XSDialog-mask')?.classList.add('unmount');
            elementToAppend.querySelector(componentRootClassName)?.addEventListener('animationend', unmount);

            setTimeout(() => {
                if (!unmounted) {
                    unmount();
                }
            }, 1000);
        }

        return {
            close: close,
        };
    }

    export function Header({
        title,
        subtitle,
        children,
        showCloseButton = true,
    }: {
        title?: React.ReactNode;
        subtitle?: React.ReactNode;
        children?: React.ReactNode;
        showCloseButton?: boolean;
    }) {
        let placeholder = (
            <>
                <div style={{ width: 'var(--rx-control)' }} />
            </>
        );
        return (
            <XSBox className={clsx('XSDialog-header', { hasChildren: children })} justify={'space-between'}>
                {!children && <div style={{ width: 'var(--rx-control)' }} />}

                <XSBox type={'column'} align={'center'}>
                    <div>{title}</div>

                    {subtitle && (
                        <XSText color={'text-secondary'} size={'sm'}>
                            {subtitle}
                        </XSText>
                    )}
                </XSBox>

                {children || showCloseButton ? (
                    <XSBox type={'row'}>
                        {children}
                        {showCloseButton && <CloseButton />}
                    </XSBox>
                ) : (
                    placeholder
                )}
            </XSBox>
        );
    }

    export function CloseButton(input: XSButtonInput) {
        const { onClick, ...rest } = input;
        const ref = React.useRef<any>(null);

        return (
            <XSButton
                ref={ref}
                onClick={(event) => {
                    ref.current?.closest?.(componentRootClassName)?.parentNode?._close?.();
                    onClick?.(event);
                }}
                icon={'close'}
                fill={'none'}
                {...rest}
            />
        );
    }

    export function Content({ className, ...rest }: XSBoxInput) {
        return <XSBox className={clsx(className, 'XSDialog-content')} {...rest} />;
    }

    export function Footer(input: XSSectionInput & { raw?: boolean }) {
        const { className, children, raw, ...rest } = input;
        const childCount = React.Children.count(children);

        return (
            <XSBox
                className={clsx(className, 'XSDialog-footer', { raw, single: childCount === 1 })}
                block={true}
                border={'top'}
                {...rest}>
                {children}
            </XSBox>
        );
    }
}

class ErrorBoundary extends React.Component<any, any> {
    state = {
        error: '',
    };

    componentDidCatch(error, info) {
        this.setState({ error: error?.message || 'Something went wrong' });
        this.props.onError();
    }

    render() {
        if (this.state.error) {
            return (
                <>
                    <XSSection>
                        <XSSection justify={'center'} gap={'lg'}>
                            <XSIcon color={'danger'}>error</XSIcon>

                            <XSText color={'danger'} weight={'bold'}>
                                Something went wrong!
                            </XSText>
                        </XSSection>

                        <XSSection justify={'center'}>{this.state.error}</XSSection>
                    </XSSection>

                    <XSSection flex={'center'}>
                        <XSDialog.CloseButton fill={'solid'}>Close</XSDialog.CloseButton>
                    </XSSection>
                </>
            );
        }

        return this.props.children;
    }
}
